import { apiPatch } from "../client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ICategory, IUpdateCategoryRequest } from "./types";

/**
 * 카테고리 수정 API 함수
 * @param id 수정할 카테고리의 ID
 * @param data 수정할 데이터
 * @returns API 응답 객체
 */
export const apiUpdateCategory = async (
  id: string,
  data: IUpdateCategoryRequest
) => {
  const response = await apiPatch<ICategory>(`/categories/${id}`, data);
  return response.data;
};

/**
 * 카테고리 순서 일괄 수정 API 함수
 * @param examType 시험 타입
 * @param subject 과목
 * @param categories 순서를 변경할 카테고리 목록
 * @returns API 응답 객체
 */
export const apiBulkUpdateCategoryOrder = async (
  examType: string,
  subject: string,
  categories: { id: string; order: number }[]
) => {
  const response = await apiPatch<ICategory[]>("/categories/bulk-order", {
    exam_type: examType,
    subject: subject,
    categories: categories,
  });
  return response.data;
};

/**
 * 카테고리 수정 React Query 훅
 * @param id 수정할 카테고리의 ID
 * @returns 뮤테이션 객체
 */
export const useUpdateCategory = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IUpdateCategoryRequest) => apiUpdateCategory(id, data),
    onSuccess: () => {
      // 카테고리 목록과 특정 카테고리 데이터 무효화
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["categories", id] });
    },
  });
};

/**
 * 카테고리 순서 일괄 수정 React Query 훅
 * @returns 뮤테이션 객체
 */
export const useBulkUpdateCategoryOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      examType,
      subject,
      categories,
    }: {
      examType: string;
      subject: string;
      categories: { id: string; order: number }[];
    }) => apiBulkUpdateCategoryOrder(examType, subject, categories),
    onSuccess: () => {
      // 카테고리 관련 모든 쿼리 무효화
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });
};

export default useUpdateCategory;
